import fontApi from "@/api/font";
import storageApi from "@/api/storage";
import InputField from "@/components/common/InputField";
import ToggleInput from "@/components/common/ToggleInput";
import { FontLoader } from "@/custom-fabric";
import { Font } from "@/interfaces/font.interface";
import { Info, Trash2, TriangleAlert } from "lucide-react";
import { Font as OTFont } from "opentype.js";
import { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import { useEffect, useState } from "react";

interface Props {
  data: Font;
}

const FontOptionId = ({ data }: Props) => {
  const [optionId, setOptionId] = useState(0);

  useEffect(() => {
    setOptionId(data.optionId);
  }, [data]);

  const onChange = (value: string) => {
    const newOptionId = Number(value.trim());
    if (!newOptionId || newOptionId === optionId) return;
    setOptionId(newOptionId);
    fontApi
      .updateOneById(data._id, { optionId: newOptionId })
      .then(() => (data.optionId = newOptionId))
      .catch(() => setOptionId(data.optionId));
  };

  return (
    <div className="hover:shadow-border group relative flex cursor-pointer rounded">
      <ToggleInput
        inputClassName="max-h-8"
        type="number"
        defaultValue={String(optionId)}
        onAfterChange={onChange}
      />
    </div>
  );
};

const FontPreview = ({ data }: Props) => {
  const [fontFace, setFontFace] = useState<FontFace>();

  useEffect(() => {
    const fontURL = storageApi.getURLFromPath(data.path);
    FontLoader.loadFontFace(fontURL)
      .then((fontFace) => setFontFace(fontFace))
      .catch(() => setFontFace(undefined));
  }, [data]);

  return (
    <div
      className="flex flex-col items-start"
      style={{ fontFamily: fontFace?.family }}
    >
      <span>ABCDabcd</span>
      <span>0123456789</span>
    </div>
  );
};

const FontName = ({ data }: Props) => {
  const onChange = (value: string) => {
    const newName = value.trim();
    if (!newName || newName === data.name) return;
    data.name = newName;
    fontApi
      .updateOneById(data._id, { name: newName })
      .then(() => {})
      .catch(() => {});
  };

  return (
    <div className="hover:shadow-border cursor-pointer rounded">
      <ToggleInput
        inputClassName="max-h-8"
        defaultValue={String(data.name)}
        onAfterChange={onChange}
      />
    </div>
  );
};

const FontActions = ({
  data,
  onDelete,
  onDetails,
}: Props & {
  onDelete?: (font: Font) => void;
  onDetails?: (font: Font) => void;
}) => {
  const onConfirmDelete = (event: any) => {
    confirmPopup({
      target: event.currentTarget,
      message: "This action cannot be undone. Confirm?",
      icon: <TriangleAlert color="red" />,
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      accept: () => onDelete?.(data),
      reject: () => {},
    });
  };
  return (
    <div className="flex gap-2">
      <Button
        severity="info"
        outlined
        style={{
          width: 28,
          height: 28,
        }}
        tooltip="Details"
        tooltipOptions={{ position: "top" }}
        icon={<Info size={18} />}
        onClick={() => onDetails?.(data)}
      />
      <Button
        severity="danger"
        outlined
        style={{
          width: 28,
          height: 28,
        }}
        tooltip="Delete"
        tooltipOptions={{ position: "top" }}
        icon={<Trash2 size={18} />}
        onClick={onConfirmDelete}
      />
    </div>
  );
};

const FontDetails = ({
  data,
  onClose,
}: {
  data?: Font;
  onClose?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [fontFace, setFontFace] = useState<FontFace>();
  const [openTypeFont, setOpenTypeFont] = useState<OTFont>();

  useEffect(() => {
    if (data) {
      setOpen(true);
      const fontURL = storageApi.getURLFromPath(data.path);
      FontLoader.loadFontFace(fontURL)
        .then((fontFace) => setFontFace(fontFace))
        .catch(() => setFontFace(undefined));
      FontLoader.loadOpenTypeFont(fontURL)
        .then((font) => setOpenTypeFont(font))
        .catch(() => setOpenTypeFont(undefined));
    } else {
      setOpen(false);
    }
  }, [data]);

  return (
    <Dialog visible={open} header="Font details" onHide={() => onClose?.()}>
      <div className="flex w-[500px] flex-col gap-4">
        <InputField
          label="Font name"
          labelWidth={120}
          inputClassName="max-h-8 min-h-8"
          type="Text"
          readOnly
          value={String(openTypeFont?.names.fullName?.en || "")}
        />
        <InputField
          label="Font family"
          labelWidth={120}
          inputClassName="max-h-8 min-h-8"
          type="Text"
          readOnly
          value={String(openTypeFont?.names.fontFamily?.en || "")}
        />
        <InputField
          label="Font subfamily"
          labelWidth={120}
          inputClassName="max-h-8 min-h-8"
          type="Text"
          readOnly
          value={String(openTypeFont?.names.fontSubfamily?.en || "")}
        />
        <InputField
          label="Version"
          labelWidth={120}
          inputClassName="max-h-8 min-h-8"
          type="Text"
          readOnly
          value={String(openTypeFont?.names.version?.en || "")}
        />
        <InputField
          label="Preview"
          labelWidth={120}
          inputClassName="max-h-8 min-h-8"
          style={{ fontFamily: fontFace?.family }}
          type="Text"
          readOnly
          value="ABCD abcd 0123456789"
        />
      </div>
    </Dialog>
  );
};

export { FontOptionId, FontPreview, FontName, FontActions, FontDetails };
